import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import {
  CollaborationProject,
  DesignProject,
  DesignProjectSubtype,
  ExtendedPlannerProject,
  Maybe,
  MultiProject,
  NBNProject,
  PlannerProject,
  Project,
  projectTypeLabelMapping,
  WORKFLOW_KEYS,
  WorkflowKey,
} from "fond/types";

/**
 * At the moment only collaboration projects use vector tiles.
 * @returns {boolean} flag indicating if the project uses vector tiles to display map data.
 */
export const projectUsesVectorTiles = (project: Maybe<Project | MultiProject>): boolean => {
  return !!project && project.EntityType === "project" && !!project.HasCustomLayerConfig;
};

/**
 * Determines if the current project supports styles to be customised via the style editor.
 * Currently we only support collaboration projects, but that may change in the future.
 *
 * @param project The project or multiproject entity
 * @returns {boolean} flag indicating if styles can be customised.
 */
export const canCustomiseStyles = (project: Maybe<Project | MultiProject>): boolean => {
  return !!project && project.EntityType === "project" && project.Type === "collaboration";
};

export const isPlannerProject = (project: Maybe<Project | MultiProject>): project is PlannerProject => {
  return !!project && project.EntityType === "project" && project.Type === "planner";
};

/**
 * Return whether `project` is a 'planner_extended' project.
 * @param project The project or multiproject entity
 */
export const isExtendedPlannerProject = (project: Maybe<Project | MultiProject>): project is ExtendedPlannerProject => {
  return isPlannerProject(project) && project.Subtype === "planner_extended";
};

export const isDesignProject = (project: Maybe<Project | MultiProject>): project is DesignProject => {
  return !!project && project.EntityType === "project" && project.Type === "design";
};

export const isNBNProject = (project: Maybe<Project | MultiProject>): project is NBNProject => {
  return isDesignProject(project) && (project.Subtype === "nbn_cost_to_serve" || project.Subtype === "nbn_idd_lite");
};

export const useIsCustomWorkflowComplete = ({ versionId, key }: { versionId?: string; key?: WorkflowKey }): boolean => {
  return useCustomWorkflow({ versionId, key }).workflowState === "COMPLETE";
};

export const useIsDesignProjectComplete = ({
  versionId,
  project,
  rootVersionId,
}: {
  versionId?: string;
  project: Maybe<Project | MultiProject>;
  rootVersionId?: string;
}): boolean => {
  const workflowKeys: Partial<Record<DesignProjectSubtype, WorkflowKey>> = {
    // NBN projects have two different workflows that determine if the project is complete based
    // on if it is the root version or an iteration.
    nbn_cost_to_serve: versionId === rootVersionId ? WORKFLOW_KEYS.NBN_COST_TO_SERVE.ROOT : WORKFLOW_KEYS.NBN_COST_TO_SERVE.ITERATION,
    nbn_idd_lite: WORKFLOW_KEYS.NBN_IDD_LITE.GENERATE_DESIGN,
    irby_small_batch: WORKFLOW_KEYS.IRBY.SMALL_BATCH_GENERATE,

    // TODO: add workflow keys for ISG projects once they are implemented. For now we consider them always complete since the workflow is not used to gate any functionality.
    // isg: WORKFLOW_KEYS.ISG.DATA_PREP,
  };

  const workflowKey = isDesignProject(project) && project.Subtype ? workflowKeys[project.Subtype] : undefined;
  return (
    useIsCustomWorkflowComplete({ versionId, key: workflowKey }) ||
    // NBN cost to serve iterations are always considered complete.
    workflowKey === WORKFLOW_KEYS.NBN_COST_TO_SERVE.ITERATION
  );
};

export const isCollaborationProject = (project: Maybe<Project | MultiProject>): project is CollaborationProject => {
  return !!project && project.EntityType === "project" && project.Type === "collaboration";
};

export const isProject = (project: Maybe<Project | MultiProject>): project is Project => {
  return project?.EntityType === "project";
};

export const isMultiProject = (project: Maybe<Project | MultiProject>): project is MultiProject => {
  return !!project && project.EntityType === "multi_project";
};

/**
 * Determine if the project can / is allowed to have versions. Projects that are part of a multiproject
 * are not allowed to have versions.
 */
export const projectCanHaveVersions = (project: Maybe<Project | MultiProject>): boolean => {
  return !!project && project.EntityType === "project" && project.MultiProjectArea === null;
};

/**
 * Returns the name of an entity (required due to inconsistent naming conventions)
 * @param project The project or multiproject entity
 * @returns {string} The entities name
 */
export const getProjectName = (project: Maybe<Project | MultiProject>): string => {
  if (!project) return "";

  // FIXME: API should return Name instead of ProjectName
  return isMultiProject(project) ? project.Name : project.ProjectName;
};

export const getProjectTypeLabel = (project: Maybe<Project | MultiProject>): string => {
  if (!project) return "";

  if (isMultiProject(project)) return "Market Lens";
  return projectTypeLabelMapping[project.Type];
};
